/**
 * @file Free-text option picker control for configured text lists.
 */

import {
  bindNativeFieldBehavior,
  NATIVE_INPUT_CLASSES,
} from "../../forms/nativeFieldStyles.js";
import {
  registerControl,
  updateControlValue,
} from "./controlCore.js";

const INVALID_SHADOW = "0 0 0 1.5px rgba(220, 38, 38, 0.22)";

let nextTextOptionId = 1;

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function normalizeOptions(options = []) {
  return (Array.isArray(options) ? options : [])
    .map((option, index) => {
      const value = option && typeof option === "object" ?
        option.value ?? option.name ?? option.label ?? "" :
        option ?? "";
      const label = option && typeof option === "object" ?
        option.label ?? option.name ?? value :
        value;
      return {
        value: String(value || "").trim(),
        label: String(label || value || "").trim(),
        order: Number(option?.order ?? index),
      };
    })
    .filter((option) => option.value)
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

function filterOptions(options, query) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery) {return options;}
  return options.filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery) ||
    option.value.toLowerCase().includes(normalizedQuery));
}

/**
 * Creates a text input with a selectable configured-option menu.
 * @param {object} config Control config.
 * @param {HTMLElement} config.root Mount root.
 * @param {string} config.name Form field name.
 * @param {string} [config.value] Initial value.
 * @param {Array<object|string>} [config.options] Suggested options.
 * @param {string} [config.placeholder] Input placeholder.
 * @param {boolean} [config.required=false] Whether the value is required.
 * @param {boolean} [config.disabled=false] Whether the control is disabled.
 * @param {Function} [config.onChange] Change callback.
 * @returns {object} Registered control handle.
 */
export function createTextOptionControl(config = {}) {
  const root = config.root;
  if (!root) {
    throw new Error("createTextOptionControl requires a root element.");
  }

  const options = normalizeOptions(config.options);
  const initialValue = String(config.value || "");
  let isDisabled = Boolean(config.disabled);
  let isRequired = Boolean(config.required);
  const menuId = `text-option-menu-${nextTextOptionId}`;
  nextTextOptionId += 1;

  root.innerHTML = `
    <div class="relative" data-text-option-shell>
      <div class="relative">
        <input
          type="text"
          name="${attrValue(config.name || "")}"
          class="${NATIVE_INPUT_CLASSES} ${isRequired ? "pr-9" : "pr-14"} disabled:opacity-100 disabled:hover:border-gray-200 disabled:focus:shadow-none"
          value="${attrValue(initialValue)}"
          placeholder="${attrValue(config.placeholder || "")}"
          autocomplete="off"
          aria-label="${attrValue(config.accessibleName || config.name || "Text option")}"
          aria-haspopup="listbox"
          aria-expanded="false"
          aria-controls="${menuId}"
          data-text-option-input
          ${isRequired ? "required" : ""}
          ${isDisabled ? "disabled" : ""}
        >
        <button
          type="button"
          class="absolute inset-y-0 right-2 inline-flex w-5 items-center justify-center text-gray-500 focus:outline-none"
          aria-label="Show options"
          data-text-option-toggle
          ${isDisabled ? "disabled" : ""}
        >
          <span
            class="material-symbols-outlined pointer-events-none text-[18px] leading-none"
            data-text-option-toggle-icon
          >expand_more</span>
        </button>
      </div>
      <div
        id="${menuId}"
        class="absolute z-[80] mt-1 hidden max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white p-1 text-sm shadow-lg"
        data-text-option-menu
      ></div>
    </div>
  `;

  const input = root.querySelector("input");
  const button = root.querySelector("button");
  const toggleIcon = root.querySelector("[data-text-option-toggle-icon]");
  const menu = root.querySelector(`#${menuId}`);
  let currentValue = initialValue;
  let isOpen = false;
  let filterQuery = "";
  let suppressNextFocusOpen = false;
  let handle = null;
  let validationObserver = null;
  let clearButton = null;

  const isInvalid = () => root.hasAttribute("data-control-invalid");

  const syncDisabledState = () => {
    input.disabled = isDisabled;
    button.disabled = isDisabled;
    root.toggleAttribute("data-control-disabled", isDisabled);
    input.classList.toggle("cursor-not-allowed", isDisabled);
    input.classList.toggle("bg-gray-100", isDisabled);
    input.classList.toggle("text-gray-400", isDisabled);
    input.classList.toggle("bg-white", !isDisabled);
    input.classList.toggle("text-gray-700", !isDisabled);
    input.classList.toggle("hover:border-[var(--dfc-brand-primary)]", !isDisabled);
    input.classList.toggle("border-gray-200", isDisabled && !isInvalid());
    button.classList.toggle("cursor-not-allowed", isDisabled);
    button.classList.toggle("text-gray-500", !isDisabled);
    button.classList.toggle("text-gray-400", isDisabled);
    if (isDisabled) {setOpen(false);}
  };

  const syncValidationState = () => {
    const invalid = isInvalid();
    input.setAttribute("aria-invalid", String(invalid));
    input.style.borderColor = invalid ? "#dc2626" : "";
    input.style.boxShadow = invalid ? INVALID_SHADOW : "";
    syncDisabledState();
  };

  const renderOptions = () => {
    const visibleOptions = filterOptions(options, filterQuery);
    if (visibleOptions.length === 0) {
      menu.innerHTML = `
        <div class="px-3 py-2 text-sm text-gray-500">No configured options. Press enter to save typed value.</div>
      `;
      return;
    }

    menu.innerHTML = `
      <div role="listbox" data-text-option-options>
        ${visibleOptions.map((option) => {
          const isSelected = option.value === currentValue;
          const selectedClasses = isSelected ?
            "font-semibold text-[var(--dfc-brand-accent)]" :
            "text-gray-700";
          return `
      <button
        type="button"
        class="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${selectedClasses}"
        data-text-option-value="${attrValue(option.value)}"
        role="option"
        aria-selected="${String(isSelected)}"
      >
        <span class="truncate">${attrValue(option.label)}</span>
        ${isSelected ? `
          <span class="material-symbols-outlined shrink-0 text-[18px] text-[var(--dfc-brand-accent)]">check</span>
        ` : ""}
      </button>
    `;
        }).join("")}
      </div>
    `;
  };

  const setOpen = (nextOpen) => {
    if (isDisabled && nextOpen) {return;}
    isOpen = Boolean(nextOpen);
    menu.classList.toggle("hidden", !isOpen);
    input.setAttribute("aria-expanded", String(isOpen));
    button.setAttribute("aria-label", isOpen ? "Hide options" : "Show options");
    if (toggleIcon) {
      toggleIcon.textContent = isOpen ? "expand_less" : "expand_more";
    }
    if (isOpen) {renderOptions();}
  };

  const setValueLocal = (value) => {
    currentValue = String(value || "");
    input.value = currentValue;
    syncClearState();
    renderOptions();
  };

  const applyValue = (value, {silent = false} = {}) => {
    updateControlValue(handle.id, value, {silent});
    if (!silent) {
      config.onChange?.(currentValue);
      root.dispatchEvent(new Event("change", {bubbles: true}));
    }
  };

  const canClear = () => !isRequired && !isDisabled && Boolean(currentValue);

  const syncClearState = () => {
    if (!clearButton) {return;}
    const show = canClear();
    clearButton.classList.toggle("hidden", !show);
    clearButton.classList.toggle("inline-flex", show);
    clearButton.disabled = !show;
    clearButton.setAttribute("aria-hidden", String(!show));
  };

  const handleOutsidePointer = (event) => {
    if (!root.contains(event.target)) {
      setOpen(false);
    }
  };

  handle = registerControl({
    root,
    name: config.name,
    value: initialValue,
    mirror: false,
    required: Boolean(config.required),
    disabled: isDisabled,
    getValue: () => currentValue,
    setValue: setValueLocal,
    validate: (value) => {
      if (isRequired && !String(value || "").trim()) {
        return {valid: false, message: "This field is required."};
      }
      return {valid: true, message: ""};
    },
    update: (updates = {}) => {
      if ("required" in updates) {
        isRequired = Boolean(updates.required);
        input.required = isRequired;
      }
      if ("disabled" in updates) {
        isDisabled = Boolean(updates.disabled);
      }
      input.classList.toggle("pr-9", isRequired);
      input.classList.toggle("pr-14", !isRequired);
      syncValidationState();
      syncClearState();
    },
    destroy: () => {
      validationObserver?.disconnect();
      validationObserver = null;
      document.removeEventListener("pointerdown", handleOutsidePointer);
    },
  });

  validationObserver = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.attributeName === "data-control-invalid")) {
      syncValidationState();
    }
  });
  validationObserver.observe(root, {attributes: true});

  if (!config.required) {
    clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.className = [
      "absolute inset-y-0 right-[29px] hidden w-5 items-center justify-center rounded-full",
      "text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none",
    ].join(" ");
    clearButton.setAttribute("aria-label", "Clear selection");
    clearButton.setAttribute("data-text-option-clear", "");
    clearButton.innerHTML = '<span class="material-symbols-outlined pointer-events-none text-[13px] leading-none">close</span>';
    input.parentElement.append(clearButton);
    clearButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      filterQuery = "";
      applyValue("");
      suppressNextFocusOpen = true;
      input.focus({preventScroll: true});
      suppressNextFocusOpen = false;
      setOpen(false);
    });
  }

  input.addEventListener("input", () => {
    if (isDisabled) {return;}
    filterQuery = input.value;
    updateControlValue(handle.id, input.value, {silent: true});
    config.onChange?.(currentValue);
    setOpen(true);
  });
  input.addEventListener("focus", () => {
    if (isDisabled) {return;}
    if (suppressNextFocusOpen) {return;}
    filterQuery = input.value === currentValue ? "" : input.value;
    if (options.length > 0) {setOpen(true);}
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    if (event.key === "Enter") {
      const visibleOptions = filterOptions(options, filterQuery);
      if (visibleOptions.length > 0) {
        event.preventDefault();
        setOpen(true);
        return;
      }

      event.preventDefault();
      applyValue(input.value || "");
      setOpen(false);
      input.blur();
    }
  });
  button.addEventListener("click", () => {
    if (isDisabled) {return;}
    const nextOpen = !isOpen;
    filterQuery = "";
    input.focus();
    setOpen(nextOpen);
  });
  menu.addEventListener("click", (event) => {
    if (isDisabled) {return;}
    const optionButton = event.target.closest("[data-text-option-value]");
    if (!optionButton) {return;}
    applyValue(optionButton.dataset.textOptionValue || "");
    setOpen(false);
  });

  document.addEventListener("pointerdown", handleOutsidePointer);

  bindNativeFieldBehavior(root);
  syncValidationState();
  syncClearState();
  updateControlValue(handle.id, initialValue, {silent: true});
  return handle;
}
